import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, Eye, EyeOff, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { auth, googleProvider } from "@/firebase";
import { createUserWithEmailAndPassword, signInWithPopup, signOut as firebaseSignOut, updateProfile } from "firebase/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  // Redirect if already logged in and verified (effect avoids setState-during-render warning + black screen)
  useEffect(() => {
    if (user && user.email_verified) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      // 1. Create Firebase auth record so the user can sign in with email/password later.
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName: name });

      // 2. Send the branded AURA verification email via our backend
      //    (intentionally NOT calling Firebase's sendEmailVerification — that template
      //     is the plain Firebase white email that users were complaining about).
      try {
        await axios.post(`${API}/auth/send-branded-verification`, {
          email,
          name,
          firebase_uid: userCredential.user.uid,
        });
      } catch (brandedErr) {
        // If our backend fails, the Firebase account still exists. Log and surface.
        console.error("[AURA Auth] Branded verification send failed:", brandedErr);
        toast.error("Account created, but we couldn't send the verification email. Please try Resend from the next screen.");
      }

      // 3. Sign out of Firebase so the user must verify before signing in.
      //    (Prevents unverified auto-login via onAuthStateChanged.)
      try { await firebaseSignOut(auth); } catch { /* ignore */ }

      setRegisteredEmail(email);
      setRegistrationComplete(true);
      toast.success("Account created! Please check your email to verify.");
    } catch (error) {
      if (error.code === "auth/email-already-in-use") {
        toast.error("This email is already registered. Try signing in.");
      } else if (error.code === "auth/weak-password") {
        toast.error("Password is too weak. Use at least 6 characters.");
      } else {
        toast.error("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setGoogleLoading(true);
    try {
      console.log('[AURA Auth] Starting Google popup sign-up...');
      const result = await signInWithPopup(auth, googleProvider);
      console.log('[AURA Auth] Popup completed, user:', result.user.email);
      // onAuthStateChanged in AuthProvider handles token exchange.
      // RegisterPage auto-navigates to /dashboard when user is set.
    } catch (error) {
      console.error('[AURA Auth] Google popup error:', error.code, error.message);
      if (error.code === 'auth/popup-blocked') {
        toast.error('Popup blocked by browser. Please allow popups for this site.');
      } else if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
        toast.error('Google sign-up failed. Please try again.');
      }
      setGoogleLoading(false);
    }
  };

  // Show verification sent success screen
  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center px-6 relative">
        <div className="noise-overlay fixed inset-0 pointer-events-none" />
        <div className="grid-bg fixed inset-0 pointer-events-none opacity-30" />
        <div className="absolute inset-0 bg-aura-glow opacity-20" />

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md relative z-10"
        >
          <Link to="/" className="flex items-center justify-center gap-3 mb-8">
            <svg width="48" height="48" viewBox="0 0 100 100" className="glow">
              <defs>
                <radialGradient id="ball-reg-success" cx="30%" cy="30%">
                  <stop offset="0%" stopColor="#444"/>
                  <stop offset="100%" stopColor="#000"/>
                </radialGradient>
              </defs>
              <circle cx="50" cy="50" r="45" fill="url(#ball-reg-success)" />
              <circle cx="50" cy="50" r="18" fill="white"/>
              <text x="50" y="58" textAnchor="middle" fontSize="22" fontWeight="bold" fill="black">8</text>
            </svg>
            <span className="font-heading text-3xl font-bold text-white">AURA</span>
          </Link>

          <div className="card-aura p-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-aura-emerald/10 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-aura-emerald" />
              </div>
            </motion.div>
            
            <h1 className="font-heading text-2xl font-bold text-white mb-2">Check Your Email</h1>
            <p className="text-aura-text-secondary mb-6">
              We've sent a verification link to:
            </p>
            <p className="text-aura-emerald font-medium mb-6 break-all">{registeredEmail}</p>
            <p className="text-aura-text-muted text-sm mb-6">
              Click the link in your email to activate your account. Then come back and sign in.
            </p>
            
            <div className="space-y-3">
              <Button 
                className="w-full btn-primary py-6"
                onClick={() => navigate("/login")}
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Button>
              <p className="text-xs text-aura-text-muted">
                Didn't receive the email? Check your spam folder.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg flex items-center justify-center px-6 relative">
      {/* Background effects */}
      <div className="noise-overlay fixed inset-0 pointer-events-none" />
      <div className="grid-bg fixed inset-0 pointer-events-none opacity-30" />
      <div className="absolute inset-0 bg-aura-glow opacity-20" />

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="register-logo">
          <svg width="48" height="48" viewBox="0 0 100 100" className="glow">
            <defs>
              <radialGradient id="ball-register" cx="30%" cy="30%">
                <stop offset="0%" stopColor="#444"/>
                <stop offset="100%" stopColor="#000"/>
              </radialGradient>
            </defs>
            <circle cx="50" cy="50" r="45" fill="url(#ball-register)" />
            <circle cx="50" cy="50" r="18" fill="white"/>
            <text x="50" y="58" textAnchor="middle" fontSize="22" fontWeight="bold" fill="black">8</text>
          </svg>
          <span className="font-heading text-3xl font-bold text-white">AURA</span>
        </Link>

        {/* Card */}
        <div className="card-aura p-8">
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl font-bold text-white mb-2">Create Account</h1>
            <p className="text-aura-text-secondary">Start your journey with AURA</p>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full mb-6 border-white/20 hover:border-white/40 bg-white/5 text-white py-6"
            onClick={handleGoogleSignup}
            disabled={googleLoading}
            data-testid="google-signup-btn"
          >
            {googleLoading ? (
              <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            ) : (
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-aura-surface text-aura-text-muted">or register with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-aura-text-secondary">Full Name</Label>
              <div className="relative input-icon-wrapper">
                <User className="input-icon w-5 h-5" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="input-aura pl-11"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-aura-text-secondary">Email</Label>
              <div className="relative input-icon-wrapper">
                <Mail className="input-icon w-5 h-5" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input-aura pl-11"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-aura-text-secondary">Password</Label>
              <div className="relative input-icon-wrapper">
                <Lock className="input-icon w-5 h-5" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-aura pl-11 pr-11"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-aura-text-muted hover:text-white transition-colors z-10"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-aura-text-muted">Must be at least 6 characters</p>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary py-6"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-aura-text-secondary">
            Already have an account?{" "}
            <Link to="/login" className="text-aura-emerald hover:text-aura-emerald-glow transition-colors" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>

        <p className="mt-6 text-center text-xs text-aura-text-muted max-w-sm mx-auto">
          By creating an account, you agree to our Terms of Service and acknowledge our responsible gambling policy.
        </p>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
